import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import PlatformShowreel from "@/components/PlatformShowreel";
import CareersShowreel from "@/components/CareersShowreel";
import {
  ArrowUpRight,
  Camera,
  Sparkles,
  Download,
  Smartphone,
  Brain,
  ListChecks,
  Mic,
  Star,
  CheckCircle2,
  ChevronRight,
  FileText,
  BellRing,
  Video,
  Mail,
  ShieldCheck,
  BarChart3,
  Users,
  Code2,
  IdCard,
  Workflow,
  CalendarClock,
  Wand2,
  ClipboardEdit,
  LayoutGrid,
  MailCheck,
  Palette,
  Eye,
  Rocket,
  GitCompare,
  TrendingUp,
  Bot,
  Calendar,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1762341107834-a3437dd0ae62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjB3b3JrcGxhY2V8ZW58MHx8fHwxNzgwMzk2NTk5fDA&ixlib=rb-4.1.0&q=85";
const DASH_IMG =
  "https://images.unsplash.com/photo-1762341121210-6bd877d766b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjB3b3JrcGxhY2V8ZW58MHx8fHwxNzgwMzk2NTk5fDA&ixlib=rb-4.1.0&q=85";
const HEAD1 =
  "https://images.unsplash.com/photo-1701096374092-bb70915fdc5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGxvb2tpbmclMjBhdCUyMGNhbWVyYXxlbnwwfHx8fDE3ODAzOTY2MTd8MA&ixlib=rb-4.1.0&q=85";
const HEAD2 =
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMGxvb2tpbmclMjBhdCUyMGNhbWVyYXxlbnwwfHx8fDE3ODAzOTY2MTd8MA&ixlib=rb-4.1.0&q=85";

const features = [
  {
    icon: Brain,
    title: "AI-generated questions",
    body:
      "Drop a job title and our model crafts a tailored set of behavioural and technical questions in seconds. Regenerate or hand-edit any question.",
  },
  {
    icon: Camera,
    title: "Async video interviews",
    body:
      "Candidates record answers from any browser. No scheduling. No timezone tetris. Two-minute cap per answer keeps things crisp.",
  },
  {
    icon: Video,
    title: "Live 1:1 camera rooms",
    body:
      "Need to chat in real time? Spin up a WebRTC camera-to-camera room and share the link — no installs, no Zoom accounts.",
  },
  {
    icon: Mic,
    title: "Auto transcription",
    body:
      "Whisper-grade transcripts attached to every answer — searchable, scoreable, and shareable with your team.",
  },
  {
    icon: ListChecks,
    title: "Objective scoring",
    body:
      "Each answer is graded on a 0–10 scale with reasoning, plus a per-answer quality preview so candidates can self-correct before submitting.",
  },
  {
    icon: GitCompare,
    title: "Head-to-head comparison",
    body:
      "Pick 2-4 candidates and see them in a side-by-side grid: résumé score, weighted score (using each role's rubric), per-question winners with 'Best' badges, transcripts and AI gap analysis. Make hiring decisions in one screen.",
  },
  {
    icon: Bot,
    title: "AI Co-pilot",
    body:
      "Ask anything about a candidate ('Would they be a good fit for a senior role?', 'Give me 3 follow-up questions') and get answers grounded in their actual interview, with confidence pills and Q-citations.",
  },
  {
    icon: TrendingUp,
    title: "Pipeline funnel analytics",
    body:
      "A 6-stage funnel right on your Dashboard: Applied → Invited → Opened → Started → Completed → Hired. Conversion KPIs and a 7/30/90-day toggle make leaks obvious at a glance.",
  },
  {
    icon: Calendar,
    title: "Google Calendar holds",
    body:
      "Connect Google Calendar once, then book one-click interview holds from any Review page. Google Meet links and candidate invites are generated automatically.",
  },
  {
    icon: FileText,
    title: "Résumé ranking + parsing",
    body:
      "Drag PDFs, DOCX or TXT files in. AI extracts name, email, phone, address, and scores every résumé against the role — strengths, gaps and printable summaries included.",
  },
  {
    icon: IdCard,
    title: "Unified applicants pipeline",
    body:
      "Every candidate across every job, in one searchable table. Auto-assigned APP-YYYY-NNNNN reference numbers, mm/dd/yyyy received dates, per-row status dropdown, and Form/Résumé badges so you spot the source instantly.",
  },
  {
    icon: ClipboardEdit,
    title: "Online application form",
    body:
      "Candidates can either upload a résumé OR fill out a structured online application — same AI scoring pipeline either way. Form path gets a violet 'Form' badge and a one-click 'Request résumé' button on each row.",
  },
  {
    icon: Workflow,
    title: "Pipeline statuses",
    body:
      "New → Awaiting Response → Interview Scheduled → Hired. Plus Not Hired and Not Interested for terminal stages — auto-flipped when you invite a candidate.",
  },
  {
    icon: LayoutGrid,
    title: "Kanban board + bulk actions",
    body:
      "Switch between Table and Kanban view. Drag candidates between columns to update status — no menus. Plus one-click batch actions across any selection: re-stage, invite to interview, or send reminders. Each action shows a results dialog listing every skipped row with a precise reason.",
  },
  {
    icon: Eye,
    title: "Editable email templates + test sends",
    body:
      "Open Settings → Preview emails to see every candidate-facing email rendered with your brand. Click 'Edit body' to rewrite invite, reminder, welcome pack, rejection, or withdrawal prose with {name}/{position}/{employer}/{ref} placeholders — then 'Test send to me' to fire a sample to your own inbox before going live. Total editorial control with zero risk.",
  },
  {
    icon: Palette,
    title: "White-label branding (shared team brand)",
    body:
      "Upload your company logo and hide every trace of Hireloop PRO from your candidate-facing pages AND emails. The logo + brand label are a SHARED team brand — any teammate on your account can update them (white-label and Welcome Pack toggles stay owner-only). Logo flows through the careers page, application form, status check, tokenized résumé-attach link, weekly digest, the Welcome Pack, plus every invite/reminder/rejection/withdrawal email.",
  },
  {
    icon: Mail,
    title: "Candidate Reply-To routing",
    body:
      "Set a Reply-To address (e.g. recruiting@yourcompany.com) so when candidates hit Reply on your invites, reminders, welcome packs or status emails, their replies land in your real inbox — not bouncing back to a noreply sender. One-click 'Send test to Reply-To' button verifies it arrives before you go live.",
  },
  {
    icon: BarChart3,
    title: "Email engagement analytics",
    body:
      "Resend delivery webhooks stamp Opened / Clicked / Delivered / Bounced timestamps on every candidate row. Dashboard widget shows your 7/30/90-day engagement KPIs with open + click rates, and Applicants can be sorted by 'Most recently opened' or 'Most recently clicked' so you can triage your warmest leads first.",
  },
  {
    icon: Sparkles,
    title: "AI live-coaching during the interview",
    body:
      "GPT-5.2 reads each candidate's résumé and the role, then surfaces 3 tailored questions the moment your live room connects — with 'Listen for…' hints so you know what signal to watch for in each answer. Tap a question to copy + mark it asked. Take notes mid-call; one button turns them into Strengths / Concerns / Next-step bullets. After the call, the recording is auto-transcribed and the AI flags concrete gaps (dodged answers, unverified claims, contradictions) with 'Ask in round 2: …' follow-ups.",
  },
  {
    icon: Download,
    title: "One-tap candidate brief PDF",
    body:
      "Click Export and your candidate review becomes a branded one-page A4 PDF — your logo, candidate name, overall score, strengths/concerns/next-step from your in-call notes, the AI gap analysis with round-2 prompts, and a per-question score table. Hiring managers love receiving these instead of reading transcripts.",
  },
  {
    icon: Smartphone,
    title: "Mobile-ready · Installable PWA",
    body:
      "Hireloop PRO works as a native-feeling app on every phone. Recruiters can tap-and-hold to Add to Home Screen for one-tap access to their pipeline — full mobile-friendly navigation, hamburger menu, and a candidate Apply flow optimized for phones (where most candidates apply from).",
  },
  {
    icon: MailCheck,
    title: "Candidate Welcome Pack",
    body:
      "When someone applies, fire an automatic branded confirmation email with their APP-YYYY-NNNNN reference, status check link, expected 1-2-3 timeline, and a one-click 'Withdraw application' tokenized button. Significantly cuts inbound 'did you receive my application?' emails.",
  },
  {
    icon: MailCheck,
    title: "Weekly stale-pipeline digest",
    body:
      "Opt in to a once-a-week summary email of your stalest candidates per stage — with one-click 'Nudge all' and 'Move to Not Interested' buttons right inside the email. No need to even open the app: act on stale invites straight from your inbox.",
  },
  {
    icon: Code2,
    title: "Embed careers + status on your site",
    body:
      "Two ready-made iframes: a careers page (apply + AI-score) and a candidate status check (paste ref# + email to see pipeline stage). Paste them on any company website.",
  },
  {
    icon: Mail,
    title: "Branded candidate auto-emails",
    body:
      "Opt-in polite emails when you mark a candidate Not Hired or Not Interested. Editable per-employer templates with {name}, {position}, {employer}, {ref} tokens, plus Preview + Send-test-to-me.",
  },
  {
    icon: Wand2,
    title: "Application reference numbers",
    body:
      "Every applicant gets an APP-YYYY-NNNNN ref shown right on the submit-confirmation screen and emailed back. Candidates can self-check status anytime.",
  },
  {
    icon: BellRing,
    title: "One-click outreach + reminders",
    body:
      "Invite your top N résumés in a single click. Polite reminder cadence (24h–7d) chases non-responders so you never do the manual nag.",
  },
  {
    icon: CalendarClock,
    title: "Annual billing · save 10%",
    body:
      "Pay monthly or annually with a 10% baked-in discount applied transparently by Stripe. Seasonal promo coupons toggle on with one env variable.",
  },
  {
    icon: BarChart3,
    title: "Pipeline + careers analytics",
    body:
      "Open jobs, candidates, completed interviews, reminder conversion, careers-page views — all at a glance. Drill into any candidate in one click.",
  },
  {
    icon: Users,
    title: "Team seats + invites",
    body:
      "Add teammates by email — seat add-ons billed by Stripe per interval. Owner controls who sees what; the unified Applicants view spans every seat's jobs.",
  },
  {
    icon: ShieldCheck,
    title: "Secure by default",
    body:
      "JWT auth with session rotation on password change, role-based admin portal, Stripe-managed billing, and rate-limited public status lookups to prevent enumeration.",
  },
];

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: 39,
    blurb: "For small teams deploying their first AI Interviews.",
    seats: 1,
    extraSeatPrice: 0,
    theme: {
      card: "bg-white text-slate-900",
      eyebrow: "text-slate-500",
      blurb: "text-slate-700",
      perk: "text-slate-800",
      check: "text-blue-700",
      seatBadge: "border-slate-300 bg-slate-50 text-slate-700",
      cta: "bg-slate-900 text-white hover:bg-slate-800",
      priceColor: "text-slate-900",
    },
    perks: [
      "10 interviews / month",
      "Auto transcription & scoring",
      "Email support",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    price: 99,
    blurb: "For growing teams with active interview pipelines.",
    seats: 2,
    extraSeatPrice: 10,
    theme: {
      card: "bg-blue-700 text-white",
      eyebrow: "text-blue-200",
      blurb: "text-blue-50",
      perk: "text-blue-50",
      check: "text-sky-200",
      seatBadge: "border-blue-300/40 bg-blue-600/40 text-white",
      cta: "bg-white text-blue-700 hover:bg-blue-50",
      priceColor: "text-white",
    },
    perks: [
      "50 interviews / month",
      "Candidate ranking & exports",
      "Priority support",
    ],
    highlight: true,
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 299,
    blurb: "For staffing agencies running interviews at scale.",
    seats: 10,
    extraSeatPrice: 10,
    theme: {
      card: "bg-slate-900 text-white",
      eyebrow: "text-amber-300",
      blurb: "text-slate-200",
      perk: "text-slate-100",
      check: "text-amber-300",
      seatBadge: "border-amber-300/40 bg-amber-300/10 text-amber-200",
      cta: "bg-amber-300 text-slate-900 hover:bg-amber-200",
      priceColor: "text-white",
    },
    perks: ["Unlimited interviews", "Team seats & SSO", "Dedicated CSM"],
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const [billingInterval, setBillingInterval] = useState("monthly");
  const [promo, setPromo] = useState({ active: false, label: "" });
  const [ciBadgeFailed, setCiBadgeFailed] = useState(false);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/plans`)
      .then(({ data }) => {
        if (data?._promo?.active) setPromo(data._promo);
      })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page" className="bg-white text-slate-900">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-20 lg:grid-cols-12 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="mb-6 inline-flex items-center gap-2 border border-slate-200 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
              <Sparkles className="h-3 w-3" /> Built for staffing agencies & employers
            </div>
            <h1 className="font-heading text-4xl font-semibold tracking-tighter text-slate-900 sm:text-5xl lg:text-7xl">
              Hire faster.<br />
              Let the <span className="text-brand-gradient">AI interview first.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-black lg:text-lg">
              Hireloop PRO is an AI-powered camera-to-camera interview platform. Drop a
              job title, share a link, and get scored, ranked, transcribed video
              answers — ready to review in minutes, not weeks.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Button
                size="lg"
                data-testid="hero-start-btn"
                onClick={() => navigate("/register")}
                className="bg-brand-gradient rounded-sm px-6 py-6 text-base font-medium text-white"
              >
                Start hiring with AI
                <ArrowUpRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                data-testid="hero-secondary-btn"
                onClick={() => navigate("/login")}
                className="rounded-sm border-blue-300 bg-blue-50 px-6 py-6 text-base text-blue-700 hover:bg-blue-100"
              >
                Sign in
              </Button>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs font-bold uppercase tracking-[0.2em] text-black">
              <div>No credit card needed</div>
              <div className="h-1 w-1 bg-slate-500" />
              <div>5-min setup</div>
              <div className="h-1 w-1 bg-slate-500" />
              <div>Whisper + GPT-5.2</div>
            </div>

            {/* Trust strip — CI + regression coverage as social proof */}
            <div
              data-testid="hero-trust-strip"
              className="mt-6 flex flex-wrap items-center gap-2 text-[10px]"
            >
              {process.env.REACT_APP_CI_BADGE_URL && !ciBadgeFailed ? (
                <a
                  data-testid="trust-badge-ci-live"
                  href={process.env.REACT_APP_CI_REPO_URL || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center"
                  title="Live GitHub Actions CI status"
                >
                  <img
                    src={process.env.REACT_APP_CI_BADGE_URL}
                    alt="Build status"
                    onError={() => setCiBadgeFailed(true)}
                    className="h-5"
                  />
                </a>
              ) : (
                <span
                  data-testid="trust-badge-ci"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-300 bg-emerald-50 px-2.5 py-1 font-bold uppercase tracking-[0.16em] text-emerald-700"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Build · passing
                </span>
              )}
              {(() => {
                const raw = process.env.REACT_APP_LAST_DEPLOY_AT;
                if (!raw) return null;
                const ts = new Date(raw);
                if (Number.isNaN(ts.getTime())) return null;
                const diffMs = Date.now() - ts.getTime();
                if (diffMs < 0) return null;
                const mins = Math.floor(diffMs / 60000);
                const hrs = Math.floor(mins / 60);
                const days = Math.floor(hrs / 24);
                let label;
                if (mins < 1) label = "just now";
                else if (mins < 60) label = `${mins}m ago`;
                else if (hrs < 24) label = `${hrs}h ago`;
                else if (days < 30) label = `${days}d ago`;
                else label = ts.toLocaleDateString();
                return (
                  <span
                    data-testid="trust-badge-deploy"
                    title={`Last deploy: ${ts.toLocaleString()}`}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-blue-300 bg-blue-50 px-2.5 py-1 font-bold uppercase tracking-[0.16em] text-blue-700"
                  >
                    <Rocket className="h-3 w-3" />
                    Latest deploy · {label}
                  </span>
                );
              })()}
              <span
                data-testid="trust-badge-tests"
                className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1 font-bold uppercase tracking-[0.16em] text-slate-700"
              >
                25 regression tests
              </span>
              <span
                data-testid="trust-badge-security"
                className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1 font-bold uppercase tracking-[0.16em] text-slate-700"
              >
                <ShieldCheck className="h-3 w-3" />
                Encrypted at rest
              </span>
              <span
                data-testid="trust-badge-uptime"
                className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-2.5 py-1 font-bold uppercase tracking-[0.16em] text-slate-700"
              >
                99.9% uptime
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative lg:col-span-5"
          >
            <PlatformShowreel />
            <div className="absolute -bottom-6 -left-6 hidden border border-slate-200 bg-white p-4 shadow-sm md:block">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                Avg. score
              </div>
              <div className="kpi-stat font-heading text-4xl font-semibold">8.4</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CAREERS EMBED — animated demo */}
      <section className="border-b border-slate-200 bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                Embed in 60 seconds
              </div>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-slate-900 lg:text-5xl">
                A careers page that scores résumés the moment they hit your inbox.
              </h2>
              <p className="mt-6 max-w-md text-base text-black">
                Paste a single iframe into your company site or a role-specific
                blog post. Candidates upload their résumé directly — the AI
                scores it against the role and drops it straight into your
                Hireloop ranking table. No more inbox archaeology.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-slate-800">
                {[
                  "Sitewide embed shows every open role automatically",
                  "Per-role embed for LinkedIn posts or role-specific landing pages",
                  "AI-scored résumés appear in your ranking table instantly",
                  "Hosted by Hireloop — no infrastructure on your side",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-blue-700" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  data-testid="careers-embed-cta"
                  onClick={() => navigate("/register")}
                  size="lg"
                  className="rounded-sm bg-blue-700 text-white hover:bg-blue-600"
                >
                  Get your embed code
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
                <a
                  href="#features"
                  className="inline-flex items-center gap-2 border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  See all features
                </a>
              </div>
            </div>
            <div className="lg:col-span-7">
              <CareersShowreel />
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="features"
        className="border-b border-slate-200 bg-slate-50 py-20 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
                Why teams switch
              </div>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-slate-900 lg:text-5xl">
                Stop watching 40 videos to find one hire.
              </h2>
              <p className="mt-6 max-w-md text-base text-black">
                Hireloop PRO watches them for you — and tells you exactly who&apos;s worth
                a callback. Every answer is transcribed, scored, and ranked.
              </p>
            </div>
            <div className="grid gap-px bg-slate-200 lg:col-span-7 lg:grid-cols-2">
              {features.map((f) => (
                <div
                  key={f.title}
                  data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group bg-white p-8 transition-colors duration-200 hover:bg-slate-50"
                >
                  <f.icon className="h-6 w-6 text-blue-700" />
                  <h3 className="mt-6 font-heading text-xl font-medium text-slate-900">
                    {f.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500">
                    {f.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT GLIMPSE */}
      <section className="border-b border-slate-200 py-20 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-6">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              Workflow
            </div>
            <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight lg:text-5xl">
              Three steps. Zero scheduling.
            </h2>
            <ol className="mt-10 space-y-6">
              {[
                {
                  n: "01",
                  t: "Create a role",
                  d: "Drop the job title. We generate tailored questions instantly.",
                },
                {
                  n: "02",
                  t: "Share the link",
                  d: "Candidates record from any browser. Mic + camera, that's it.",
                },
                {
                  n: "03",
                  t: "Review scores",
                  d: "Transcripts, scores, and feedback in one ranked dashboard.",
                },
              ].map((s) => (
                <li key={s.n} className="flex gap-6 border-t border-slate-200 pt-6">
                  <div className="font-heading text-2xl font-semibold text-blue-700">
                    {s.n}
                  </div>
                  <div>
                    <div className="font-heading text-lg font-medium">{s.t}</div>
                    <div className="mt-1 text-sm text-slate-500">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
          <div className="grid gap-4 lg:col-span-6">
            <div className="border border-slate-200 bg-white">
              {/* Modern dashboard mockup — replaces stock photo */}
              <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-red-400" />
                <span className="h-2 w-2 rounded-full bg-amber-400" />
                <span className="h-2 w-2 rounded-full bg-emerald-400" />
                <div className="ml-2 flex-1 truncate rounded-sm bg-white px-2 py-0.5 text-[9px] font-mono text-slate-500 border border-slate-200">
                  hireloop.pro/dashboard
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
                  Pipeline overview
                </div>
                <div className="mt-2 grid grid-cols-3 gap-px bg-slate-200">
                  {[
                    { l: "Open Jobs", v: "4", bg: "bg-emerald-600" },
                    { l: "Candidates", v: "27", bg: "bg-blue-700" },
                    { l: "Completed", v: "13", bg: "bg-slate-900" },
                  ].map((k) => (
                    <div key={k.l} className={`${k.bg} p-3 text-white`}>
                      <div className="text-[8px] font-bold uppercase tracking-[0.2em] opacity-80">{k.l}</div>
                      <div className="mt-1 text-2xl font-bold">{k.v}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 space-y-1.5">
                  {[
                    { t: "Senior Backend Engineer", c: 12 },
                    { t: "Frontend Developer", c: 9 },
                    { t: "Product Designer", c: 6 },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between border border-slate-900 px-3 py-2">
                      <span className="text-xs font-medium text-slate-900">{r.t}</span>
                      <span className="text-[10px] font-bold text-red-600">{r.c} candidates</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  name: "Maya Patel",
                  photo:
                    "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&q=80&auto=format&fit=crop",
                  score: "9.1",
                  scoreColor: "text-emerald-600",
                  badgeBorder: "border-emerald-300/60",
                  role: "Senior Backend",
                  hits: ["Python · 5y", "AWS · Lead", "Distributed sys"],
                },
                {
                  name: "Diego Rivas",
                  photo:
                    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80&auto=format&fit=crop",
                  score: "7.8",
                  scoreColor: "text-blue-700",
                  badgeBorder: "border-blue-300/60",
                  role: "Frontend Eng",
                  hits: ["React · 6y", "TypeScript", "Design systems"],
                },
              ].map((c) => (
                <div key={c.name} className="border border-slate-200 bg-white p-4">
                  <div className="relative aspect-square w-full overflow-hidden">
                    <img
                      src={c.photo}
                      alt={c.name}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div
                      className={`absolute bottom-2 left-2 border ${c.badgeBorder} bg-black/55 backdrop-blur-sm px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.15em] text-white`}
                    >
                      {c.role}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className={`flex items-center gap-1 text-sm font-semibold ${c.scoreColor}`}>
                      <Star className="h-3 w-3 fill-current" /> {c.score}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.hits.map((h) => (
                      <span key={h} className="border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-700">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="border-b border-slate-200 bg-slate-50 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
              Pricing
            </div>
            <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight lg:text-5xl">
              Pay for the interviews, not the seats.
            </h2>
            <p className="mt-4 text-base text-black">
              Cancel anytime. All plans include AI question generation,
              transcription, and scoring.
            </p>
          </div>

          {/* Billing interval toggle */}
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <div
              data-testid="pricing-interval-toggle"
              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white p-1 shadow-sm"
            >
              <button
                data-testid="pricing-interval-monthly"
                type="button"
                onClick={() => setBillingInterval("monthly")}
                className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition ${
                  billingInterval === "monthly"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Monthly
              </button>
              <button
                data-testid="pricing-interval-annual"
                type="button"
                onClick={() => setBillingInterval("annual")}
                className={`relative rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition ${
                  billingInterval === "annual"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Annual
                <span
                  data-testid="pricing-annual-savings-badge"
                  className={`ml-2 inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9px] font-bold tracking-normal ${
                    billingInterval === "annual"
                      ? "bg-emerald-400 text-emerald-950"
                      : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  Save 10%
                </span>
              </button>
            </div>
            {promo.active && billingInterval === "annual" && (
              <div
                data-testid="pricing-annual-promo-pill"
                className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800"
              >
                Limited-time: {promo.label}
              </div>
            )}
          </div>

          <div className="mt-12 grid gap-px bg-slate-200 lg:grid-cols-3">
            {PLANS.map((p) => {
              const t = p.theme;
              const isAnnual = billingInterval === "annual";
              const displayPrice = isAnnual
                ? (Math.round(p.price * 12 * 0.9 / 12 * 100) / 100).toFixed(2)
                : Number(p.price).toFixed(2);
              const annualTotal = (
                Math.round(p.price * 12 * 0.9 * 100) / 100
              ).toFixed(2);
              return (
                <div
                  key={p.id}
                  data-testid={`pricing-${p.id}`}
                  className={`relative flex flex-col p-8 ${t.card} ${
                    p.highlight ? "lg:-mt-4 lg:pb-12 lg:pt-12" : ""
                  }`}
                >
                  {p.highlight && (
                    <div className="absolute right-6 top-6 bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-700">
                      Most popular
                    </div>
                  )}
                  <div className={`text-[10px] font-bold uppercase tracking-[0.22em] ${t.eyebrow}`}>
                    Plan
                  </div>
                  <div className="mt-1 font-heading text-2xl font-medium">
                    {p.name}
                  </div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span
                      data-testid={`pricing-${p.id}-price`}
                      className={`font-heading text-5xl font-semibold ${t.priceColor}`}
                    >
                      ${displayPrice}
                    </span>
                    <span className={`text-sm ${t.blurb}`}>/month</span>
                  </div>
                  {isAnnual ? (
                    <div
                      data-testid={`pricing-${p.id}-annual-detail`}
                      className={`mt-1 text-xs ${t.blurb}`}
                    >
                      <span className="line-through opacity-60">
                        ${Number(p.price).toFixed(2)}/mo
                      </span>
                      <span className="ml-2 font-semibold">
                        ${annualTotal}/yr billed annually · save 10%
                      </span>
                    </div>
                  ) : null}
                  <p className={`mt-3 text-sm ${t.blurb}`}>{p.blurb}</p>

                  <div
                    data-testid={`pricing-${p.id}-seats`}
                    className={`mt-5 inline-flex items-center gap-2 self-start border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] ${t.seatBadge}`}
                  >
                    <Users className="h-3 w-3" />
                    {p.seats === 1
                      ? "1 user seat included"
                      : `Up to ${p.seats} user seats included`}
                  </div>
                  {p.extraSeatPrice > 0 && (
                    <div
                      data-testid={`pricing-${p.id}-extra-seat`}
                      className={`mt-2 text-[11px] ${t.blurb}`}
                    >
                      +${Number(p.extraSeatPrice).toFixed(2)}/mo per additional user
                      {isAnnual ? " (billed annually, 10% off)" : ""}
                    </div>
                  )}

                  <ul className="mt-6 space-y-3 text-sm">
                    {p.perks.map((perk) => (
                      <li key={perk} className="flex items-start gap-2">
                        <CheckCircle2
                          className={`mt-0.5 h-4 w-4 flex-none ${t.check}`}
                        />
                        <span className={t.perk}>{perk}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`pricing-${p.id}-cta`}
                    onClick={() =>
                      navigate(
                        `/register?plan=${p.id}&interval=${billingInterval}`
                      )
                    }
                    className={`mt-8 rounded-sm font-semibold ${t.cta}`}
                  >
                    Choose {p.name}
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-slate-200 bg-slate-900 py-20 text-white lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <h2 className="font-heading text-3xl font-semibold tracking-tight lg:text-6xl">
                Your next hire is already waiting.<br />
                <span className="text-brand-gradient">You just haven&apos;t met them yet.</span>
              </h2>
            </div>
            <div className="flex flex-wrap items-center gap-4 lg:col-span-4 lg:justify-end">
              <Button
                size="lg"
                data-testid="footer-cta-btn"
                onClick={() => navigate("/register")}
                className="bg-brand-gradient rounded-sm px-6 py-6 text-white"
              >
                Create free account
                <ArrowUpRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-b border-slate-200 bg-white py-10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 md:flex-row md:items-center">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black">
            © 2026 Hireloop PRO | Encore Business Services Group, LLC
          </div>
          <div className="flex gap-6 text-sm text-slate-500">
            <Link to="/login">Sign in</Link>
            <Link to="/register">Get started</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
